import { useState } from 'react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { KeyRound, Power, RefreshCw } from 'lucide-react';
import { METRICS } from '@raptor/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingState } from '@/components/LoadingState';
import { ApiError } from '@/lib/api/client';
import { useIssueToken, useRevokeToken, useUpdateWell, useWellDetail } from '../api';
import type { IssuedWellToken } from '../types';

function ago(iso: string | null): string {
  return iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : 'never';
}

interface Props {
  wellId: string | null;
  siteId: string;
  onClose: () => void;
  // Bubble a freshly-issued token up so the parent can show it exactly once.
  onIssued: (issued: IssuedWellToken) => void;
}

export function WellDetailDialog({ wellId, siteId, onClose, onIssued }: Props) {
  const { data: detail, isLoading } = useWellDetail(wellId);
  const updateWell = useUpdateWell(wellId ?? '', siteId);
  const issueToken = useIssueToken(wellId ?? '', siteId);
  const revokeToken = useRevokeToken(wellId ?? '', siteId);
  const [confirm, setConfirm] = useState<'rotate' | 'revoke' | null>(null);

  const toggleActive = async () => {
    if (!detail) return;
    try {
      await updateWell.mutateAsync({ isActive: !detail.well.isActive });
      toast.success(detail.well.isActive ? 'Well deactivated' : 'Well activated');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update well');
    }
  };

  const issue = async () => {
    try {
      const issued = await issueToken.mutateAsync();
      setConfirm(null);
      onIssued(issued);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to issue token');
    }
  };

  const revoke = async () => {
    try {
      await revokeToken.mutateAsync();
      setConfirm(null);
      toast.success('Token revoked');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to revoke token');
    }
  };

  return (
    <>
      <Dialog open={wellId !== null} onOpenChange={(open) => !open && onClose()}>
        <DialogContent data-testid="well-detail-dialog" className="max-w-lg">
          {isLoading || !detail ? (
            <LoadingState rows={4} />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.well.name}
                  <StatusBadge
                    variant={detail.well.isActive ? 'success' : 'neutral'}
                    label={detail.well.isActive ? 'Active' : 'Inactive'}
                  />
                </DialogTitle>
                <DialogDescription>
                  <code>{detail.well.wellCode}</code> · {detail.site.name} · {detail.customer.name}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                {/* Last reading */}
                <div className="rounded-md border p-3">
                  <div className="mb-1 text-xs font-medium text-muted-foreground">Last reading</div>
                  {detail.lastReading ? (
                    <div className="space-y-0.5">
                      <div>
                        {METRICS.temperature.label}:{' '}
                        {detail.lastReading.temperature.toFixed(METRICS.temperature.precision)}{' '}
                        {METRICS.temperature.unit} · {METRICS.pressure.label}:{' '}
                        {detail.lastReading.pressure} {METRICS.pressure.unit}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {ago(detail.lastReading.recordedAt)} · {detail.totalReadings} total
                      </div>
                    </div>
                  ) : (
                    <div className="text-muted-foreground">No readings yet</div>
                  )}
                </div>

                {/* Token panel */}
                <div className="rounded-md border p-3" data-testid="well-token-panel">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-xs font-medium text-muted-foreground">Device token</div>
                    {detail.activeToken ? (
                      <StatusBadge variant="success" label="Active" />
                    ) : (
                      <StatusBadge variant="neutral" label="None" />
                    )}
                  </div>
                  {detail.activeToken ? (
                    <div className="space-y-2">
                      <div className="text-xs text-muted-foreground">
                        <code>{detail.activeToken.tokenPrefix}…</code> · last used{' '}
                        {ago(detail.activeToken.lastUsedAt)}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="well-token-rotate"
                          onClick={() => setConfirm('rotate')}
                        >
                          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Rotate
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="well-token-revoke"
                          onClick={() => setConfirm('revoke')}
                        >
                          Revoke
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      data-testid="well-token-issue"
                      disabled={issueToken.isPending}
                      onClick={() => void issue()}
                    >
                      <KeyRound className="mr-1 h-3.5 w-3.5" /> Issue token
                    </Button>
                  )}
                </div>
              </div>

              <DialogFooter className="sm:justify-between">
                <Button
                  variant="outline"
                  data-testid="well-toggle-active"
                  disabled={updateWell.isPending}
                  onClick={() => void toggleActive()}
                >
                  <Power className="mr-1 h-3.5 w-3.5" />
                  {detail.well.isActive ? 'Deactivate' : 'Activate'}
                </Button>
                <Button variant="ghost" onClick={onClose}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Rotate / revoke confirmation */}
      <Dialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent data-testid="well-token-confirm">
          <DialogHeader>
            <DialogTitle>
              {confirm === 'rotate' ? 'Rotate device token?' : 'Revoke device token?'}
            </DialogTitle>
            <DialogDescription>
              {confirm === 'rotate'
                ? 'The current token is revoked immediately and the device will stop reporting until it is reconfigured with the new token.'
                : 'The current token is revoked immediately and the device will stop reporting. No replacement is issued.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant={confirm === 'revoke' ? 'destructive' : 'default'}
              data-testid="well-token-confirm-action"
              disabled={issueToken.isPending || revokeToken.isPending}
              onClick={() => void (confirm === 'rotate' ? issue() : revoke())}
            >
              {confirm === 'rotate' ? 'Rotate' : 'Revoke'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
